import intelliJetNetService, { QueryBuilder } from '../intellijetnet'
import { AircraftAudit } from '../../types/intellijetnet'
import { executeQuery } from './utils'

export class AircraftAuditService {
  private static readonly TABLE_NAME = 'aircraft_audit'

  /**
   * Get query builder for aircraft audit records
   */
  static query(): QueryBuilder {
    return intelliJetNetService.from(this.TABLE_NAME)
  }

  /**
   * Get audit records for a specific aircraft
   */
  static async getByAircraftId(aircraftId: number, limit = 50): Promise<AircraftAudit[]> {
    return executeQuery<AircraftAudit>(
      this.query()
        .eq('aircraft_id', aircraftId)
        .order('changed_at', false)
        .limit(limit)
    )
  }

  /**
   * Get recent audit records for multiple aircraft
   */
  static async getByAircraftIds(aircraftIds: number[], days = 30): Promise<AircraftAudit[]> {
    if (aircraftIds.length === 0) return []
    
    const date = new Date()
    date.setDate(date.getDate() - days)
    
    return executeQuery<AircraftAudit>(
      this.query()
        .in('aircraft_id', aircraftIds)
        .gte('changed_at', date.toISOString())
        .order('changed_at', false)
    )
  }

  /**
   * Get price change audit records for an aircraft since a specific date
   */
  static async getPriceChanges(aircraftId: number, sinceDate?: string): Promise<AircraftAudit[]> {
    let query = this.query()
      .eq('aircraft_id', aircraftId)
      .eq('operation', 'UPDATE')
      .order('changed_at', false)

    if (sinceDate) {
      query = query.gte('changed_at', sinceDate)
    }

    const records = await executeQuery<AircraftAudit>(query)
    
    // Filter for records that have asking price changes
    return records.filter(record => {
      // Check if this is a price change by comparing old_values and new_values
      if (!record.old_values || !record.new_values) return false

      // Check if asking price changed
      const oldAsking = record.old_values.asking
      const newAsking = record.new_values.asking

      if (oldAsking !== newAsking) return true

      // Also check for take_price changes
      const oldTake = record.old_values.take_price
      const newTake = record.new_values.take_price

      return oldTake !== newTake
    })
  }

  /**
   * Get the most recent price change for an aircraft
   */
  static async getLatestPriceChange(aircraftId: number): Promise<AircraftAudit | null> {
    const priceChanges = await this.getPriceChanges(aircraftId)
    return priceChanges.length > 0 ? priceChanges[0] : null
  }

  /**
   * Check if aircraft has price changes since a specific date
   */
  static async hasPriceChangesSince(aircraftId: number, sinceDate: string): Promise<boolean> {
    const priceChanges = await this.getPriceChanges(aircraftId, sinceDate)
    return priceChanges.length > 0
  }

  /**
   * Get the first listing date for an aircraft (when it was first marked for sale)
   */
  static async getFirstListingDate(aircraftId: number): Promise<string | null> {
    const records = await executeQuery<AircraftAudit>(
      this.query()
        .eq('aircraft_id', aircraftId)
        .order('changed_at', true) // Ascending to get earliest first
    )

    // Look for the first record where forsale changed to true
    for (const record of records) {
      const changedFields = record.changed_fields
      const newValues = record.new_values
      
      if (changedFields && newValues && 
          changedFields.hasOwnProperty('forsale') && 
          newValues.forsale === true) {
        return record.changed_at
      }
    }

    return null
  }

  /**
   * Get audit records for multiple aircraft with price changes in a date range
   */
  static async getBulkPriceChanges(
    aircraftIds: number[], 
    fromDate?: string, 
    toDate?: string
  ): Promise<Map<number, AircraftAudit[]>> {
    if (aircraftIds.length === 0) return new Map()

    let query = this.query()
      .in('aircraft_id', aircraftIds)
      .eq('operation', 'UPDATE')
      .order('changed_at', false)

    if (fromDate) {
      query = query.gte('changed_at', fromDate)
    }
    if (toDate) {
      query = query.lte('changed_at', toDate)
    }

    const records = await executeQuery<AircraftAudit>(query)
    
    // Filter for price changes and group by aircraft_id
    const priceChangeRecords = records.filter(record => {
      const changedFields = record.changed_fields
      return changedFields && (
        changedFields.hasOwnProperty('asking') ||
        changedFields.hasOwnProperty('asking_price')
      )
    })

    const groupedRecords = new Map<number, AircraftAudit[]>()
    
    for (const record of priceChangeRecords) {
      if (record.aircraft_id) {
        const existing = groupedRecords.get(record.aircraft_id) || []
        existing.push(record)
        groupedRecords.set(record.aircraft_id, existing)
      }
    }

    return groupedRecords
  }

  /**
   * Get recent audit activity summary
   */
  static async getRecentActivity(days = 7, limit = 100): Promise<AircraftAudit[]> {
    const date = new Date()
    date.setDate(date.getDate() - days)
    
    return executeQuery<AircraftAudit>(
      this.query()
        .gte('changed_at', date.toISOString())
        .order('changed_at', false)
        .limit(limit)
    )
  }

  /**
   * Get audit statistics for a date range
   */
  static async getStatistics(fromDate?: string, toDate?: string): Promise<{
    total: number
    priceChanges: number
    newListings: number
    statusChanges: number
  }> {
    let query = this.query()

    if (fromDate) {
      query = query.gte('changed_at', fromDate)
    }
    if (toDate) {
      query = query.lte('changed_at', toDate)
    }

    const records = await executeQuery<AircraftAudit>(query)
    
    let priceChanges = 0
    let newListings = 0
    let statusChanges = 0

    for (const record of records) {
      const changedFields = record.changed_fields
      if (changedFields) {
        if (changedFields.hasOwnProperty('asking') || changedFields.hasOwnProperty('asking_price')) {
          priceChanges++
        }
        if (changedFields.hasOwnProperty('forsale')) {
          newListings++
        }
        if (changedFields.hasOwnProperty('status')) {
          statusChanges++
        }
      }
    }

    return {
      total: records.length,
      priceChanges,
      newListings,
      statusChanges
    }
  }
}

export default AircraftAuditService
